/**
 * Create Setup Intent Actor
 *
 * Creates a Stripe Setup Intent for $0 payments (card verification).
 * Used when price is $0 to set up payment method without charging.
 */

import { datadogLogs } from "@datadog/browser-logs";
import { fromPromise } from "xstate";
import { z } from "zod";
import { getKioskId } from "../../lib/kiosk-identity";

/**
 * Input Schema (What this actor needs)
 */
const CreateSetupIntentInputSchema = z.object({
  user: z
    .object({
      id: z.string(),
      fname: z.string(),
      lname: z.string(),
    })
    .nullable(),
  consumer: z
    .object({
      id: z.string(),
      stripeCustomerId: z.string().optional().nullable(),
    })
    .optional(),
  creationSource: z.string().optional(),
  // Whether to use card_present (POS terminal) or card (web form)
  cardPresent: z.boolean().optional().default(true),
});

type CreateSetupIntentInput = z.infer<typeof CreateSetupIntentInputSchema>;

/**
 * Success Schema (What this actor returns on success)
 */
const CreateSetupIntentSuccessSchema = z.object({
  setupIntentId: z.string(),
  clientSecret: z.string(),
  customerOptions: z
    .object({
      customer: z.string(),
      ephemeralKey: z.string(),
    })
    .optional(),
});

type CreateSetupIntentSuccess = z.infer<typeof CreateSetupIntentSuccessSchema>;

/**
 * Error Schema (What this actor returns on failure)
 */
const CreateSetupIntentErrorSchema = z.object({
  code: z.enum([
    "INVALID_INPUT",
    "NETWORK_ERROR",
    "BACKEND_ERROR",
    "VALIDATION_ERROR",
    "UNKNOWN_ERROR",
  ]),
  message: z.string(),
  retryable: z.boolean(),
  details: z.unknown().optional(),
});

type CreateSetupIntentError = z.infer<typeof CreateSetupIntentErrorSchema>;

/**
 * Actor metadata
 */
export interface ActorMetadata {
  displayName: string;
  description: string;
  category: "data" | "communication" | "auth" | "payment";
  inputSchema: z.ZodSchema;
  successSchema: z.ZodSchema;
  errorSchema: z.ZodSchema;
}

/**
 * Actor with metadata wrapper
 */
export interface ActorWithMetadata {
  impl: ReturnType<
    typeof fromPromise<CreateSetupIntentSuccess, CreateSetupIntentInput>
  >;
  metadata: ActorMetadata;
}

/**
 * Create Setup Intent Actor Implementation
 *
 * Creates a Stripe Setup Intent for $0 payments.
 */
export const createSetupIntentActor: ActorWithMetadata = {
  impl: fromPromise<CreateSetupIntentSuccess, CreateSetupIntentInput>(
    async ({ input }) => {
      // Validate input
      const validated = CreateSetupIntentInputSchema.parse(input);

      try {
        // Call API to create setup intent
        const response = await fetch("/api/stripe/create-setup-intent", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user: validated.user,
            consumer: validated.consumer,
            cardPresent: validated.cardPresent, // true for POS terminal, false for web form
            collectedBy: "kiosk",
            creationSource: validated.creationSource || "kiosk",
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message ||
              `HTTP ${response.status}: ${response.statusText}`,
          );
        }

        const data = await response.json();

        // Validate response
        const result = CreateSetupIntentSuccessSchema.parse(data);

        datadogLogs.logger.info("[Fleet] Setup intent created", {
          action: "setup_intent_created",
          metadata: {
            kioskId: getKioskId(),
            consumerId: validated.consumer?.id ?? null,
            setupIntentId: result.setupIntentId,
          },
        });

        return result;
      } catch (error) {
        // Parse error
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";

        const parsedError: CreateSetupIntentError = {
          code: "BACKEND_ERROR",
          message: errorMessage,
          retryable: true,
          details: error,
        };

        datadogLogs.logger.error("[Fleet] Setup intent failed", {
          action: "setup_intent_failed",
          metadata: {
            kioskId: getKioskId(),
            consumerId: validated.consumer?.id ?? null,
            errorCode: parsedError.code ?? "UNKNOWN_ERROR",
            errorMessage: errorMessage,
          },
        });

        throw parsedError;
      }
    },
  ),
  metadata: {
    displayName: "createSetupIntent",
    description:
      "Create Stripe Setup Intent for $0 payments (card verification)",
    category: "payment",
    inputSchema: CreateSetupIntentInputSchema,
    successSchema: CreateSetupIntentSuccessSchema,
    errorSchema: CreateSetupIntentErrorSchema,
  },
};
